import { useState, useEffect, useMemo, useRef } from "react";
import { Wallet, Plus, X, CheckCircle, Download, Trash2, Search, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import api, { money, errMsg } from "../lib/api";
import CustomSelect from "../components/ui/CustomSelect";
import DatePicker from "../components/ui/DatePicker";
import { todayIST, monthIST } from "../lib/datetime";
import useAutoRefresh from "../lib/useAutoRefresh";

const role = () => {
  try {
    return (JSON.parse(localStorage.getItem("lss_user") || "{}").role || "owner").toLowerCase();
  } catch {
    return "owner";
  }
};

const TYPES = [
  { value: "expense", label: "Expense (cash out)" },
  { value: "income", label: "Cash In (receipt)" },
  { value: "contra_in", label: "Contra In (bank → cash)" },
  { value: "contra_out", label: "Contra Out (cash → bank)" },
];

export default function CashBook() {
  const isOwner = role() === "owner";
  const isManager = role() === "manager";
  const [entries, setEntries] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [month, setMonth] = useState(monthIST());
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [balance, setBalance] = useState({ cash_balance: 0, negative: false, pending_count: 0 });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [form, setForm] = useState({ date: todayIST(), entry_type: "expense", ledger_id: "", amount: "", paid_to: "", narration: "", proof: "", proof_name: "" });
  const [newLedger, setNewLedger] = useState("");
  const [staffNames, setStaffNames] = useState([]);
  const proofRef = useRef(null);

  // Tab switcher: Daily Cash Entries vs POS vs Bank Recon
  const [activeTab, setActiveTab] = useState("entries"); // "entries" | "pos_recon"
  const [reconView, setReconView] = useState("cash"); // "cash" | "upi"
  const _fom = new Date();
  _fom.setDate(1);
  const _iso = (d) => d.toISOString().slice(0, 10);
  const [recFrom, setRecFrom] = useState(_iso(_fom));
  const [recTo, setRecTo] = useState(_iso(new Date()));
  const [posRecon, setPosRecon] = useState(null);
  const [upiReconData, setUpiReconData] = useState(null);
  const [posLoading, setPosLoading] = useState(false);

  const fetchPosRecon = async (silent = false) => {
    if (!recFrom || !recTo || recFrom > recTo) return;
    if (!silent) setPosLoading(true);
    try {
      const prPromise = api.get(`/banking/pos-recon?date_from=${recFrom}&date_to=${recTo}`);
      const txPromise = api.get(`/pos/transactions?limit=100000`);
      const acctsPromise = api.get(`/banking/accounts`);

      const [pr, txRes, acctsRes] = await Promise.all([prPromise, txPromise, acctsPromise]);
      setPosRecon(pr.data);

      const bId = acctsRes.data?.accounts?.[0]?.id || "";
      const bLinesRes = bId ? await api.get(`/banking/lines?bank_id=${bId}&status=`) : { data: { lines: [] } };

      const allTx = txRes.data || [];
      const bankLines = bLinesRes.data?.lines || [];

      // Bank UPI by date (0513300A0461793)
      const bankUpiByDate = {};
      bankLines.forEach((l) => {
        const amt = Number(l.credit || 0);
        if (amt && (l.narration || "").includes("0513300A0461793")) {
          const d = (l.date || "").slice(0, 10);
          bankUpiByDate[d] = Number(((bankUpiByDate[d] || 0) + amt).toFixed(2));
        }
      });

      // POS digital by date (total_collection - cash)
      const posDigByDate = {};
      allTx.forEach((t) => {
        const d = (t.date || "").slice(0, 10);
        const dig = Math.max(0, Number(t.total_collection || 0) - Number(t.cash || 0));
        posDigByDate[d] = Number(((posDigByDate[d] || 0) + dig).toFixed(2));
      });

      // All dates in range
      const days = [];
      let cur = new Date(recFrom);
      const end = new Date(recTo);
      while (cur <= end) {
        days.push(cur.toISOString().slice(0, 10));
        cur.setDate(cur.getDate() + 1);
      }

      // 1 Sep 2026 UPI settlement from 31-Aug sales: added to opening balance
      const upiOpening = Number(bankUpiByDate["2026-09-01"] || 45986.27);
      // 30 Sep 2026 UPI settlement credited 1-Oct: added to closing balance
      const upiClosing = Number(bankUpiByDate["2026-10-01"] || 31476.22);
      let totPosDig = 0;
      let totBankUpi = 0;
      const upiRows = [];

      days.forEach((d) => {
        const nextD = new Date(d);
        nextD.setDate(nextD.getDate() + 1);
        const nextDStr = nextD.toISOString().slice(0, 10);

        const pVal = Number((posDigByDate[d] || 0).toFixed(2));
        const bVal = Number((bankUpiByDate[nextDStr] || 0).toFixed(2));
        const diff = Number((pVal - bVal).toFixed(2));

        totPosDig += pVal;
        totBankUpi += bVal;

        let st = bVal > 0 ? "Reconciled (T+1)" : (pVal > 0 ? "Pending Settlement" : "—");
        if (d === "2026-09-30" && bVal > 0) {
          st = "Added to 30-Sep Closing";
        }

        upiRows.push({
          pos_date: d,
          pos_digital: pVal,
          bank_date: nextDStr,
          bank_upi: bVal,
          diff: diff,
          status: st,
        });
      });

      setUpiReconData({
        opening_upi: upiOpening,
        closing_upi: upiClosing,
        total_pos_digital: Number(totPosDig.toFixed(2)),
        total_bank_upi: Number(totBankUpi.toFixed(2)),
        total_mdr: Number((totPosDig - totBankUpi).toFixed(2)),
        days: upiRows,
      });
    } catch (e) {
      if (!silent) toast.error(errMsg(e));
      else console.error(e);
    } finally {
      if (!silent) setPosLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "pos_recon") {
      fetchPosRecon();
    }
  }, [activeTab, recFrom, recTo]);

  // `silent` is what keeps a background refresh still: the 30-second poll and
  // the refocus reload repaint the rows and the balance in place rather than
  // blanking the table to a spinner every half minute. Only an explicit month
  // change shows it.
  // It must also NOT discard a half-completed batch selection — the rows an
  // owner has ticked to approve together survive the poll, and only a month
  // change resets them.
  const fetchAll = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [e, l, b] = await Promise.all([
        api.get(`/cash/entries?month=${month}`),
        api.get("/accounts/chart"),
        api.get("/cash/balance"),
      ]);
      setEntries(e.data?.entries || []);
      setLedgers(l.data?.ledgers || []);
      setBalance(b.data || { cash_balance: 0, negative: false, pending_count: 0 });
      try {
        const s = await api.get("/staff");
        setStaffNames((s.data || []).map((x) => x.name).filter(Boolean).sort());
      } catch (_) { /* staff dropdown is a convenience; book works without it */ }
      if (!silent) setSelected(new Set());
    } catch (err) {
      // a background failure must not pop a toast over a window being read
      if (!silent) toast.error(errMsg(err));
      else console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [month]);
  // Approvals, receipts and expenses land from any machine — keep the book live
  // with no refresh button anywhere on the screen.
  useAutoRefresh(() => {
    if (activeTab === "entries") fetchAll(true);
    else if (activeTab === "pos_recon") fetchPosRecon(true);
  }, 30000);

  // Status tab + free-text search, applied together. Search matches narration,
  // Paid To and ledger name so a word from a bill narrows the rows instantly —
  // and because Export/Select-all both read `filtered`, a search is what gets
  // exported and batch-selected too.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (statusFilter !== "All" && e.status !== statusFilter.toLowerCase()) return false;
      if (!q) return true;
      return [e.narration, e.paid_to, e.ledger_name].some((f) => String(f || "").toLowerCase().includes(q));
    });
  }, [entries, statusFilter, search]);

  const dayTotal = useMemo(() => {
    const today = todayIST();
    let out = 0, inn = 0;
    for (const e of entries) {
      if (e.date !== today || e.status === "rejected") continue;
      if (e.entry_type === "expense" || e.entry_type === "contra_out") out += e.amount || 0;
      else inn += e.amount || 0;
    }
    return { out, inn };
  }, [entries]);

  const handleProof = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Bill photo must be under 2 MB");
      if (proofRef.current) proofRef.current.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, proof: reader.result, proof_name: file.name }));
      toast.success(`Bill attached: ${file.name}`);
    };
    reader.onerror = () => toast.error("Could not read file");
    reader.readAsDataURL(file);
  };

  const handleQuickLedger = async () => {
    const nm = newLedger.trim();
    if (!nm) return;
    try {
      const res = await api.post("/accounts/ledger", { name: nm, grp: "Expense" });
      const led = res.data?.ledger;
      const l = await api.get("/accounts/chart");
      setLedgers(l.data?.ledgers || []);
      if (led) setForm((prev) => ({ ...prev, ledger_id: led.id }));
      setNewLedger("");
      toast.success(`Ledger "${nm}" created`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ledger_id || !(Number(form.amount) > 0)) {
      toast.error("Ledger and positive amount are required");
      return;
    }
    setSaving(true);
    try {
      const url = form.entry_type === "expense" ? "/cash/expense" : "/cash/receipt";
      await api.post(url, { ...form, amount: Number(form.amount), entry_type: form.entry_type });
      toast.success("Cash entry recorded — pending approval");
      setShowModal(false);
      setForm({ date: todayIST(), entry_type: "expense", ledger_id: "", amount: "", paid_to: "", narration: "", proof: "", proof_name: "" });
      if (proofRef.current) proofRef.current.value = "";
      fetchAll();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const doApprove = async (action) => {
    if (selected.size === 0) {
      toast.error("Select entries first");
      return;
    }
    try {
      const res = await api.post("/cash/approve", { ids: [...selected], action });
      toast.success(`${action === "approved" ? "Approved" : "Rejected"} ${res.data?.approved ?? 0} entries`);
      fetchAll();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete cash entry of ₹${Number(entry.amount || 0).toLocaleString()} (${entry.narration || entry.ledger_name})?`)) return;
    try {
      await api.delete(`/cash/entries/${entry.id}`);
      toast.success("Cash entry deleted");
      fetchAll();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const doDeleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selected.size} selected cash entries?`)) return;
    try {
      const res = await api.post("/cash/delete-batch", { ids: [...selected] });
      toast.success(`Deleted ${res.data?.deleted || 0} cash entries`);
      setSelected(new Set());
      fetchAll();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const toggleSel = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-950 flex items-center gap-2">
            <Wallet className="w-7 h-7 text-amber-700" />
            Daily Cash Book
          </h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">
            Petty cash expenses &amp; receipts • Bill proofs • POS &amp; Bank reconciliation audit
          </p>
        </div>
        {activeTab === "entries" ? (
          <div className="flex flex-wrap items-center gap-3">
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="lss-input px-4 py-2 text-sm" />
            <button
              onClick={() => {
                if (!filtered.length) return toast.error("No entries to export");
                const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
                const rows = [
                  ["Date", "Type", "Ledger", "Amount (INR)", "Paid To", "Narration", "Status"].join(","),
                  ...filtered.map((e) => [e.date, e.entry_type, e.ledger_name, e.amount, e.paid_to, e.narration, e.status].map(q).join(",")),
                ];
                const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `Daily_Cash_Book_${month}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success(`Exported ${filtered.length} cash book entries!`);
              }}
              className="px-4 py-2 text-sm flex items-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold transition-all"
            >
              <Download className="w-4 h-4" /> Export
            </button>
            <button onClick={() => setShowModal(true)} className="lss-btn-gold px-4 py-2 text-sm flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Entry
            </button>
          </div>
        ) : (
          <div className="flex items-end gap-2 shrink-0">
            <DatePicker label="From" value={recFrom} onChange={(e) => setRecFrom(e.target.value)} testId="pos-from" />
            <span className="text-slate-400 font-bold pb-2">→</span>
            <DatePicker label="To" value={recTo} onChange={(e) => setRecTo(e.target.value)} testId="pos-to" />
          </div>
        )}
      </div>

      {/* Primary Tab Navigation: Daily Cash Entries vs POS vs Bank Recon */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <button
          onClick={() => setActiveTab("entries")}
          className={`px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-xl transition-all shrink-0 ${
            activeTab === "entries"
              ? "bg-slate-950 text-amber-300 shadow-md"
              : "text-slate-700 hover:text-slate-950 hover:bg-slate-100"
          }`}
        >
          Daily Cash Entries
        </button>
        <button
          onClick={() => setActiveTab("pos_recon")}
          className={`px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-xl transition-all shrink-0 ${
            activeTab === "pos_recon"
              ? "bg-slate-950 text-amber-300 shadow-md"
              : "text-slate-700 hover:text-slate-950 hover:bg-slate-100"
          }`}
        >
          POS vs Bank Recon
        </button>
      </div>

      {activeTab === "entries" && (
      <>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="lss-card p-4">
          <span className="lss-overline block">Today Out</span>
          <span className="text-2xl font-extrabold text-rose-700 tabular">{money(dayTotal.out)}</span>
        </div>
        <div className="lss-card p-4">
          <span className="lss-overline block">Today In</span>
          <span className="text-2xl font-extrabold text-emerald-800 tabular">{money(dayTotal.inn)}</span>
        </div>
        <div className="lss-card p-4">
          <span className="lss-overline block">Pending Approval</span>
          <span className="text-2xl font-extrabold text-amber-800 tabular">{balance.pending_count}</span>
        </div>
      </div>

      <div className="lss-card p-3 sm:px-4 sm:py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin pb-1 sm:pb-0">
          {["All", "Pending", "Approved", "Rejected"].map((t) => (
            <button
              key={t}
              onClick={() => setStatusFilter(t)}
              className={`px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-xl transition-all shrink-0 ${
                statusFilter === t ? "bg-slate-950 text-amber-300 shadow-md" : "text-slate-700 hover:text-slate-950 hover:bg-slate-100"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 sm:ml-auto shrink-0">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search narration, paid to, ledger…"
              aria-label="Search cash entries"
              data-testid="cash-search"
              className="lss-input lss-search py-2 text-xs w-52 sm:w-64"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {isOwner && (
            <>
              <button onClick={() => doApprove("approved")} className="lss-btn-gold px-3 py-2 text-xs font-bold uppercase mr-1">Approve ({selected.size})</button>
              <button onClick={() => doApprove("rejected")} className="lss-btn-outline px-3 py-2 text-xs font-bold uppercase mr-1">Reject</button>
            </>
          )}
          {(isOwner || isManager) && selected.size > 0 && (
            <button onClick={doDeleteSelected} className="px-3 py-2 text-xs font-bold uppercase rounded-xl border border-rose-200 text-rose-700 bg-rose-50 hover:bg-rose-100 transition-colors flex items-center gap-1.5">
              <Trash2 className="w-3.5 h-3.5" /> Delete ({selected.size})
            </button>
          )}
        </div>
      </div>

      <div className="lss-card overflow-hidden">
        <div className="overflow-auto max-h-[560px]">
          <table className="lss-table min-w-[940px]">
            <thead>
              <tr>
                <th><input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length} onChange={() => setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map((e) => e.id)))} /></th>
                <th>Date</th>
                <th>Type</th>
                <th>Ledger</th>
                <th className="text-right">Amount</th>
                <th>Paid To</th>
                <th>Narration</th>
                <th className="text-center">Bill</th>
                <th className="text-center">Status</th>
                {(isOwner || isManager) && <th className="text-center">Action</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50/80">
                  <td><input type="checkbox" checked={selected.has(e.id)} onChange={() => toggleSel(e.id)} /></td>
                  <td className="tabular font-semibold whitespace-nowrap">{e.date}</td>
                  <td><span className={`lss-badge text-[10px] ${e.entry_type === "expense" ? "bg-rose-100 text-rose-900 border-rose-300" : e.entry_type === "income" ? "bg-emerald-100 text-emerald-900 border-emerald-300" : "bg-sky-100 text-sky-950 border-sky-300"}`}>{e.entry_type.replace("_", " ")}</span></td>
                  <td className="text-xs font-bold">{e.ledger_name}</td>
                  <td className="text-right tabular font-bold">{money(e.amount)}</td>
                  <td className="text-xs">{e.paid_to || "—"}</td>
                  <td className="text-xs text-slate-600 max-w-[220px] truncate" title={e.narration}>{e.narration || "—"}</td>
                  <td className="text-center">
                    {e.has_proof ? (
                      <a href={`/api/cash/proof/${e.id}`} target="_blank" rel="noreferrer" className="text-[11px] font-bold text-sky-800 underline">View</a>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="text-center"><span className={`lss-badge text-[10px] ${e.status === "approved" ? "bg-emerald-600 text-white border-emerald-700" : e.status === "rejected" ? "bg-slate-200 text-slate-600 border-slate-300" : "bg-amber-100 text-amber-950 border-amber-300"}`}>{e.status}</span></td>
                  {(isOwner || isManager) && (
                    <td className="text-center">
                      {(isOwner || e.status === "pending") && (
                        <button
                          onClick={() => handleDelete(e)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                          title="Delete cash entry"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={isOwner || isManager ? 10 : 9} className="text-center text-slate-500 font-medium py-8 text-xs">{loading ? "Loading..." : (search.trim() || statusFilter !== "All") ? "No matching entries." : "No cash entries this month."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {activeTab === "pos_recon" && (
        <div className="space-y-6">
          {(!recFrom || !recTo || recFrom > recTo) && (
            <div className="bg-amber-50 border border-amber-300 rounded-2xl px-6 py-3 text-sm font-bold text-amber-900 text-center">
              Select the correct date range (From date must be on or before To date).
            </div>
          )}

          {/* Sub-tab switcher. Owner, 30 Oct 2026: the UPI Online Settlements
              sub-tab is hidden from Daily Cash Book > POS vs Bank, so the Cash
              Drawer Recon is the only view. reconView stays (defaults "cash",
              nothing sets "upi" now) so the colleague's UPI panel below still
              compiles — it is simply unreachable. The UPI opening/closing
              balance logic is untouched: it still feeds the balances. */}
          <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
            <button
              onClick={() => setReconView("cash")}
              className={`px-4 py-2 text-xs font-extrabold uppercase tracking-wider rounded-xl transition ${
                reconView === "cash"
                  ? "bg-slate-950 text-amber-300 shadow-md"
                  : "text-slate-600 hover:text-slate-950 hover:bg-slate-100"
              }`}
            >
              Cash Drawer Recon (4030 Deposits)
            </button>
          </div>

          {reconView === "cash" && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="lss-card p-4">
                  <span className="lss-overline block">Opening Counter Cash</span>
                  <span className="text-2xl font-extrabold text-amber-900 tabular">
                    {money(posRecon?.opening_cash || 0)}
                  </span>
                  <span className="block text-[11px] font-semibold text-slate-500 mt-1">
                    Anchored 1 Sep 2026: ₹89,634.00
                  </span>
                </div>
                <div className="lss-card p-4">
                  <span className="lss-overline block">Daily Cash Book Expenses</span>
                  <span className="text-2xl font-extrabold text-rose-700 tabular">
                    {money((posRecon?.total_daily_expense || 0) + (posRecon?.total_advances || 0))}
                  </span>
                  <span className="block text-[11px] font-semibold text-slate-500 mt-1">
                    Expenses + salary advances
                  </span>
                </div>
                <div className="lss-card p-4">
                  <span className="lss-overline block">Cash In Hand (Closing)</span>
                  <span className="text-2xl font-extrabold text-slate-950 tabular">
                    {money(posRecon?.undeposited_now || 0)}
                  </span>
                  <span className="block text-[11px] font-semibold text-slate-500 mt-1">
                    Undeposited cash balance
                  </span>
                </div>
              </div>

              <div className="lss-card overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70 flex flex-wrap items-center justify-between gap-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Reconciliation Audit — POS vs Bank {posRecon?.month || ""} (cash deposits 4030)
                  </span>
                  <span className="text-xs text-slate-500 font-semibold flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span>Opening counter cash <b className="text-amber-900 tabular">{money(posRecon?.opening_cash || 0)}</b></span>
                    <span>·</span>
                    <span>Cash in hand (closing) <b className="text-slate-900 tabular">{money(posRecon?.undeposited_now || 0)}</b></span>
                    <span>·</span>
                    <span>Daily Cash Book expenses <b className="text-rose-900 tabular">{money((posRecon?.total_daily_expense || 0) + (posRecon?.total_advances || 0))}</b></span>
                  </span>
                </div>
                <div className="overflow-auto max-h-[560px]">
                  <table className="lss-table min-w-[760px]">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="text-right">POS Cash ₹</th>
                        <th className="text-right">Daily Cash Book Expense ₹</th>
                        <th className="text-right">Bulk Deposit ₹</th>
                        <th className="text-right">Closing ₹</th>
                      </tr>
                    </thead>
                    <tbody>
                      {posRecon?.opening_cash > 0 && (
                        <tr className="bg-amber-50/70 font-semibold border-b border-amber-200/80">
                          <td className="tabular font-extrabold text-amber-950">
                            Opening Balance
                          </td>
                          <td colSpan={3} className="text-xs text-amber-900 italic font-medium px-4">
                            Cash counter opening balance brought forward (anchored 1 Sep 2026: ₹89,634.00)
                          </td>
                          <td className="text-right tabular font-black text-amber-950">
                            {money(posRecon.opening_cash)}
                          </td>
                        </tr>
                      )}
                      {(posRecon?.days || []).map((d) => (
                        <tr key={d.date} className="hover:bg-slate-50/80">
                          <td className="tabular font-bold">{d.date}</td>
                          <td className="text-right tabular">{money(d.pos_cash)}</td>
                          <td className="text-right tabular font-bold text-rose-900">
                            {money((d.daily_expense || 0) + (d.salary_advance || 0))}
                          </td>
                          <td className="text-right tabular text-sky-900">{money(d.bank_cash_deposit)}</td>
                          <td className={`text-right tabular font-bold ${d.undeposited_cash > 0 ? "text-amber-800" : "text-slate-400"}`}>
                            {money(d.undeposited_cash || 0)}
                          </td>
                        </tr>
                      ))}
                      {(posRecon?.days || []).length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center text-slate-500 font-medium py-8 text-xs">
                            {posLoading ? "Loading POS vs Bank audit..." : "No POS or bank activity this period."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {reconView === "upi" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="lss-card p-4 border-l-4 border-l-amber-500 bg-amber-50/50">
                  <div className="flex items-center justify-between">
                    <span className="lss-overline text-amber-900">1 Sep UPI Opening</span>
                    <span className="text-[10px] font-extrabold uppercase bg-amber-200/80 text-amber-950 px-2 py-0.5 rounded-full">
                      Added to Opening
                    </span>
                  </div>
                  <span className="text-xl font-extrabold text-amber-950 tabular block mt-1">
                    {money(upiReconData?.opening_upi || 45986.27)}
                  </span>
                  <span className="block text-[10px] font-semibold text-amber-900/80 mt-1">
                    31-Aug sales credited 1-Sep (T+1)
                  </span>
                </div>

                <div className="lss-card p-4 border-l-4 border-l-amber-600 bg-amber-50/50">
                  <div className="flex items-center justify-between">
                    <span className="lss-overline text-amber-900">30 Sep UPI Closing</span>
                    <span className="text-[10px] font-extrabold uppercase bg-amber-200/80 text-amber-950 px-2 py-0.5 rounded-full">
                      Added to Closing
                    </span>
                  </div>
                  <span className="text-xl font-extrabold text-amber-950 tabular block mt-1">
                    {money(upiReconData?.closing_upi || 31476.22)}
                  </span>
                  <span className="block text-[10px] font-semibold text-amber-900/80 mt-1">
                    30-Sep sales credited 1-Oct (T+1)
                  </span>
                </div>

                <div className="lss-card p-4">
                  <span className="lss-overline block">POS Digital Sales</span>
                  <span className="text-xl font-extrabold text-slate-950 tabular block mt-1">
                    {money(upiReconData?.total_pos_digital || 0)}
                  </span>
                  <span className="block text-[10px] font-semibold text-slate-500 mt-1">
                    Total Card &amp; UPI from POS
                  </span>
                </div>

                <div className="lss-card p-4">
                  <span className="lss-overline block">Bank UPI Settled</span>
                  <span className="text-xl font-extrabold text-emerald-800 tabular block mt-1">
                    {money(upiReconData?.total_bank_upi || 0)}
                  </span>
                  <span className="block text-[10px] font-semibold text-slate-500 mt-1">
                    PNB batch credits (0513300A0461793)
                  </span>
                </div>

                <div className="lss-card p-4">
                  <span className="lss-overline block">MDR &amp; Gateway Fee</span>
                  <span className="text-xl font-extrabold text-rose-700 tabular block mt-1">
                    {money(upiReconData?.total_mdr || 0)}
                  </span>
                  <span className="block text-[10px] font-semibold text-slate-500 mt-1">
                    Net processing fee (~0.44%)
                  </span>
                </div>
              </div>

              <div className="lss-card overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-900 block">
                      UPI &amp; Online Settlement Audit — POS vs Bank (Terminal 0513300A0461793)
                    </span>
                    <span className="text-[11px] text-slate-500 font-medium mt-0.5 block">
                      UPI online payments arrive next business day (T+1) of credit day · 1 Sep settlement belongs to August opening · 30 Sep settlement belongs to September closing
                    </span>
                  </div>
                  <span className="text-xs text-emerald-800 font-extrabold flex items-center gap-1.5 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200">
                    <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                    T+1 Auto-Reconciled
                  </span>
                </div>

                <div className="overflow-auto max-h-[560px]">
                  <table className="lss-table min-w-[760px]">
                    <thead>
                      <tr>
                        <th>POS Sales Date</th>
                        <th className="text-right">POS Digital Sales ₹</th>
                        <th>Bank Settlement Date (T+1)</th>
                        <th className="text-right">Bank UPI Credit ₹</th>
                        <th className="text-right">MDR / Diff ₹</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Special 1 Sep Opening Row */}
                      {(!recFrom || recFrom <= "2026-09-01") && (!recTo || recTo >= "2026-09-01") && (
                        <tr className="bg-amber-50/80 font-semibold border-b border-amber-300">
                          <td className="tabular font-black text-amber-950">
                            1 Sep 2026 Opening
                          </td>
                          <td className="text-right tabular font-bold text-amber-900">
                            —
                          </td>
                          <td className="tabular font-black text-amber-950">
                            2026-09-01
                          </td>
                          <td className="text-right tabular font-black text-amber-950">
                            {money(upiReconData?.opening_upi || 45986.27)}
                          </td>
                          <td className="text-right tabular font-semibold text-slate-400">
                            —
                          </td>
                          <td className="text-center">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-amber-200 text-amber-950 border border-amber-300">
                              Added to 1-Sep Opening
                            </span>
                          </td>
                        </tr>
                      )}

                      {(upiReconData?.days || []).map((d) => (
                        <tr
                          key={d.pos_date}
                          className={
                            d.pos_date === "2026-09-30"
                              ? "bg-amber-50/80 font-bold border-y border-amber-300"
                              : "hover:bg-slate-50/80"
                          }
                        >
                          <td className="tabular font-bold text-xs">
                            {d.pos_date}
                            {d.pos_date === "2026-09-30" && (
                              <span className="ml-1 text-[10px] text-amber-900 font-extrabold">(Closing)</span>
                            )}
                          </td>
                          <td className="text-right tabular text-xs font-semibold">{money(d.pos_digital)}</td>
                          <td className="tabular text-xs text-slate-600">{d.bank_date}</td>
                          <td className="text-right tabular text-xs font-extrabold text-emerald-800">
                            {d.bank_upi > 0 ? money(d.bank_upi) : "—"}
                          </td>
                          <td className={`text-right tabular text-xs font-bold ${d.diff > 0 ? "text-rose-700" : "text-slate-500"}`}>
                            {d.bank_upi > 0 ? money(d.diff) : "—"}
                          </td>
                          <td className="text-center">
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${
                                d.status.includes("Closing")
                                  ? "bg-amber-200 text-amber-950 border border-amber-300"
                                  : d.status.includes("Reconciled")
                                  ? "bg-emerald-100 text-emerald-900 border border-emerald-300"
                                  : d.status === "—"
                                  ? "bg-slate-100 text-slate-500"
                                  : "bg-amber-100 text-amber-900 border border-amber-300"
                              }`}
                            >
                              {d.status}
                            </span>
                          </td>
                        </tr>
                      ))}

                      {!(upiReconData?.days || []).length && (
                        <tr>
                          <td colSpan={6} className="text-center text-slate-500 font-medium py-8 text-xs">
                            {posLoading ? "Loading UPI settlement audit..." : "No UPI transactions in this period."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-lg p-6 space-y-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="font-extrabold text-slate-950">New Cash Entry</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Date</label>
                  <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="lss-input w-full text-sm" required />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Type</label>
                  <select value={form.entry_type} onChange={(e) => setForm({ ...form, entry_type: e.target.value })} className="lss-input w-full text-sm">
                    {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Ledger</label>
                <CustomSelect options={ledgers.map((l) => ({ label: `${l.name} (${l.grp})`, value: l.id }))} value={form.ledger_id} onChange={(v) => setForm({ ...form, ledger_id: v })} placeholder="Select ledger..." searchable />
                {isOwner && (
                  <div className="flex gap-2 mt-2">
                    <input value={newLedger} onChange={(e) => setNewLedger(e.target.value)} placeholder="Or create ledger on the fly..." className="lss-input flex-1 text-xs" />
                    <button type="button" onClick={handleQuickLedger} className="lss-btn-outline px-3 py-1.5 text-xs whitespace-nowrap">+ Ledger</button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Amount ₹</label>
                  <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="lss-input w-full text-sm tabular" required />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Paid To / From</label>
                  {(() => {
                    const sel = (ledgers || []).find((x) => x.id === form.ledger_id);
                    const isAdvance = sel && sel.name === "Staff Salary Advances";
                    if (!isAdvance) {
                      return <input value={form.paid_to} onChange={(e) => setForm({ ...form, paid_to: e.target.value })} placeholder="Vendor / person" className="lss-input w-full text-sm" />;
                    }
                    return (
                      <select value={staffNames.includes(form.paid_to) ? form.paid_to : ""} onChange={(e) => {
                        const nm = e.target.value;
                        setForm((prev) => ({ ...prev, paid_to: nm, narration: prev.narration || (nm ? `ADVANCE [${nm}]` : prev.narration) }));
                      }} className="lss-input w-full text-sm font-semibold" required>
                        <option value="">Select staff…</option>
                        {staffNames.map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    );
                  })()}
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Narration</label>
                <input value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} placeholder="What was this for..." className="lss-input w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Bill Photo (≤2 MB)</label>
                <input type="file" ref={proofRef} onChange={handleProof} accept="image/*,.pdf" className="hidden" />
                <button type="button" onClick={() => proofRef.current?.click()} className={`w-full px-3 py-2 text-xs font-bold rounded-[10px] border transition ${form.proof ? "bg-emerald-50 text-emerald-900 border-emerald-300" : "bg-white text-slate-600 border-slate-300"}`}>
                  {form.proof_name ? `✓ ${form.proof_name.slice(0, 24)}` : "Attach Bill"}
                </button>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowModal(false)} className="lss-btn-outline px-4 py-2 text-xs">Cancel</button>
                <button type="submit" disabled={saving} className="lss-btn-gold px-5 py-2 text-xs disabled:opacity-50">{saving ? "Saving..." : "Save Entry ✓"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
